import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { api, formatBytes } from '../lib/api';
import { Bell, User, AlertTriangle, Trash2, Check, Loader2, CreditCard, Sparkles, ExternalLink } from 'lucide-react';

// Paddle configuration (sandbox)
const PADDLE_CLIENT_TOKEN = 'test_5a885dc759cad74d1a5866d52c4';
const PADDLE_PRICES = {
  starter: {
    monthly: 'pri_01kb68wydzwf9039xq219t6n9t',
    annual: 'pri_01kb68wz49pj8btzcweqtb8h5k',
  },
  professional: {
    monthly: 'pri_01kb68wzjq9f3k261p48dwsrze',
    annual: 'pri_01kb68x00rmd3zjgd17pgwz68x',
  },
};

declare global {
  interface Window {
    Paddle: {
      Environment: {
        set: (env: 'sandbox' | 'production') => void;
      };
      Initialize: (config: { token: string; eventCallback?: (event: PaddleEvent) => void }) => void;
      Checkout: {
        open: (config: {
          items: { priceId: string; quantity: number }[];
          customer?: { email?: string };
          customData?: Record<string, unknown>;
          settings?: {
            displayMode?: 'overlay' | 'inline';
            theme?: 'light' | 'dark';
            locale?: string;
            successUrl?: string;
          };
        }) => void;
      };
    };
  }
}

interface PaddleEvent {
  name: string;
  data?: {
    customer?: {
      id: string;
    };
    subscription_id?: string;
  };
}

interface SettingsResponse {
  success: boolean;
  data: {
    notification_frequency: 'hourly' | 'six_hourly' | 'daily';
    change_threshold: number;
    tier: 'free' | 'starter' | 'professional';
    email: string;
  };
}

interface StatsResponse {
  success: boolean;
  data: {
    device_count: number;
    device_limit: number | null;
    snapshot_count: number;
    notification_count_30d: number;
    total_disk_space: number;
    total_used_space: number;
    tier: string;
  };
}

interface SubscriptionResponse {
  success: boolean;
  data: {
    tier: string;
    status: 'active' | 'canceled' | 'past_due' | 'paused' | 'trialing' | null;
    price_id: string | null;
    next_billed_at: string | null;
    canceled_at: string | null;
    max_devices: number;
    has_subscription: boolean;
  };
}

export default function Settings() {
  const { user, logout, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'professional'>('starter');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [paddleLoaded, setPaddleLoaded] = useState(false);

  // Load Paddle.js
  useEffect(() => {
    if (document.querySelector('script[src*="paddle.com"]')) {
      if (window.Paddle) {
        setPaddleLoaded(true);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      if (window.Paddle) {
        window.Paddle.Environment.set('sandbox');
        window.Paddle.Initialize({
          token: PADDLE_CLIENT_TOKEN,
          eventCallback: async (event: PaddleEvent) => {
            console.log('Paddle event:', event);
            if (event.name === 'checkout.completed') {
              // Link the customer to the user
              if (event.data?.customer?.id) {
                try {
                  await api.post('/api/subscriptions/link-customer', {
                    customer_id: event.data.customer.id,
                  });
                  // Refresh user data
                  refreshUser();
                  queryClient.invalidateQueries({ queryKey: ['subscription'] });
                  queryClient.invalidateQueries({ queryKey: ['settings'] });
                } catch (error) {
                  console.error('Failed to link customer:', error);
                }
              }
              setShowUpgradeModal(false);
            }
          },
        });
        setPaddleLoaded(true);
      }
    };
    document.body.appendChild(script);
  }, [refreshUser, queryClient]);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsResponse>('/api/settings'),
  });

  const { data: stats } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.get<StatsResponse>('/api/settings/stats'),
  });

  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get<SubscriptionResponse>('/api/subscriptions/current'),
  });

  const updateSettings = useMutation({
    mutationFn: (data: { notification_frequency?: string; change_threshold?: number }) =>
      api.put('/api/settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      refreshUser();
    },
  });

  const deleteAccount = useMutation({
    mutationFn: () => api.delete('/api/settings/account'),
    onSuccess: () => {
      logout();
    },
  });

  const frequencyOptions = [
    { value: 'hourly', label: 'Hourly', premium: true },
    { value: 'six_hourly', label: 'Every 6 hours', premium: true },
    { value: 'daily', label: 'Daily', premium: false },
  ];

  const handleFrequencyChange = (frequency: string) => {
    if (settings?.data.tier === 'free' && frequency !== 'daily') {
      setShowUpgradeModal(true);
      return;
    }
    updateSettings.mutate({ notification_frequency: frequency });
  };

  const handleThresholdChange = (threshold: number) => {
    updateSettings.mutate({ change_threshold: threshold });
  };

  const handleUpgrade = (plan: 'starter' | 'professional') => {
    if (!paddleLoaded) {
      alert('Payment system is loading. Please try again in a moment.');
      return;
    }

    const priceId = PADDLE_PRICES[plan][billingCycle];

    window.Paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: user?.email ? { email: user.email } : undefined,
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        successUrl: window.location.href + '?checkout=success',
      },
    });
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'professional':
        return 'bg-violet-100 text-violet-700 border-violet-200';
      case 'starter':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-neutral-100 text-neutral-600 border-neutral-200';
    }
  };

  const getStatusBadgeColor = (status: string | null) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-700';
      case 'past_due':
        return 'bg-amber-100 text-amber-700';
      case 'canceled':
        return 'bg-red-100 text-red-700';
      case 'paused':
        return 'bg-neutral-100 text-neutral-600';
      default:
        return 'bg-neutral-100 text-neutral-600';
    }
  };

  const getPlanPrice = (plan: 'starter' | 'professional', cycle: 'monthly' | 'annual') => {
    const prices = {
      starter: { monthly: 5.99, annual: 49.90 },
      professional: { monthly: 10.99, annual: 99.90 },
    };
    return prices[plan][cycle];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Settings</h1>
        <p className="text-neutral-500 mt-1">Manage your account and notification preferences</p>
      </div>

      {/* Subscription Card */}
      <div className="rounded-2xl bg-white p-6 shadow-sm border border-neutral-200/50">
        <div className="flex items-center gap-3 mb-6">
          <div className="rounded-xl bg-violet-50 p-2.5 text-violet-600">
            <CreditCard className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-900">Subscription</h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 mb-6">
          <div>
            <p className="text-sm text-neutral-500">Current Plan</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium border ${getTierBadgeColor(settings?.data.tier || 'free')}`}>
                {(settings?.data.tier || 'free').charAt(0).toUpperCase() + (settings?.data.tier || 'free').slice(1)}
              </span>
              {subscription?.data.status && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getStatusBadgeColor(subscription.data.status)}`}>
                  {subscription.data.status.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm text-neutral-500">Server Limit</p>
            <p className="font-medium text-neutral-900">
              {stats?.data.device_count || 0} / {subscription?.data.max_devices || 1} servers
            </p>
          </div>
          {subscription?.data.next_billed_at && (
            <div>
              <p className="text-sm text-neutral-500">Next Billing Date</p>
              <p className="font-medium text-neutral-900">
                {new Date(subscription.data.next_billed_at).toLocaleDateString()}
              </p>
            </div>
          )}
          {subscription?.data.canceled_at && (
            <div>
              <p className="text-sm text-neutral-500">Canceled On</p>
              <p className="font-medium text-red-600">
                {new Date(subscription.data.canceled_at).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {settings?.data.tier === 'free' ? (
          <div className="p-4 rounded-xl bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-violet-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-neutral-900">Upgrade to unlock more features</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Get more servers, advanced alerts, and AI-powered insights.
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 text-sm font-medium text-white shadow-sm hover:shadow-md transition-all"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex gap-3">
            <a
              href="https://customer-portal.paddle.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Manage Subscription
            </a>
            {settings?.data.tier !== 'professional' && (
              <button
                onClick={() => {
                  setSelectedPlan('professional');
                  setShowUpgradeModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 text-sm font-medium text-white shadow-sm hover:shadow-md transition-all"
              >
                <Sparkles className="h-4 w-4" />
                Upgrade to Professional
              </button>
            )}
          </div>
        )}
      </div>

      {/* Account Overview */}
      <div className="rounded-2xl bg-white p-6 shadow-sm border border-neutral-200/50">
        <div className="flex items-center gap-3 mb-6">
          <div className="rounded-xl bg-primary-50 p-2.5 text-primary-600">
            <User className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-900">Account</h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-neutral-500">Email</p>
            <p className="font-medium text-neutral-900">{user?.email}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-500">Name</p>
            <p className="font-medium text-neutral-900">{user?.name || '-'}</p>
          </div>
        </div>

        {stats?.data && (
          <div className="mt-6 pt-6 border-t border-neutral-100">
            <h3 className="text-sm font-medium text-neutral-700 mb-4">Usage Stats</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl bg-neutral-50 p-4">
                <p className="text-2xl font-bold text-neutral-900">
                  {stats.data.snapshot_count.toLocaleString()}
                </p>
                <p className="text-sm text-neutral-500">Total Snapshots</p>
              </div>
              <div className="rounded-xl bg-neutral-50 p-4">
                <p className="text-2xl font-bold text-neutral-900">
                  {formatBytes(stats.data.total_used_space)}
                </p>
                <p className="text-sm text-neutral-500">Total Monitored</p>
              </div>
              <div className="rounded-xl bg-neutral-50 p-4">
                <p className="text-2xl font-bold text-neutral-900">
                  {stats.data.notification_count_30d}
                </p>
                <p className="text-sm text-neutral-500">Alerts (30 days)</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification Settings */}
      <div className="rounded-2xl bg-white p-6 shadow-sm border border-neutral-200/50">
        <div className="flex items-center gap-3 mb-6">
          <div className="rounded-xl bg-primary-50 p-2.5 text-primary-600">
            <Bell className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-900">Notifications</h2>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-3 block">
              Notification Frequency
            </label>
            <div className="flex flex-wrap gap-3">
              {frequencyOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleFrequencyChange(option.value)}
                  disabled={updateSettings.isPending}
                  className={`relative rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
                    settings?.data.notification_frequency === option.value
                      ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  } ${option.premium && settings?.data.tier === 'free' ? 'opacity-60' : ''}`}
                >
                  {option.label}
                  {option.premium && settings?.data.tier === 'free' && (
                    <span className="ml-1 text-xs">✨</span>
                  )}
                  {settings?.data.notification_frequency === option.value && (
                    <Check className="h-4 w-4 inline-block ml-2" />
                  )}
                </button>
              ))}
            </div>
            {settings?.data.tier === 'free' && (
              <p className="mt-2 text-xs text-neutral-500">
                ✨ Hourly and 6-hourly notifications require a paid plan.
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700 mb-3 block">
              Change Threshold: {settings?.data.change_threshold || 10}%
            </label>
            <p className="text-sm text-neutral-500 mb-3">
              Get notified when a file or folder changes by this percentage or more.
            </p>
            <input
              type="range"
              min="5"
              max="50"
              step="5"
              value={settings?.data.change_threshold || 10}
              onChange={(e) => handleThresholdChange(parseInt(e.target.value))}
              className="w-full accent-primary-600"
            />
            <div className="flex justify-between text-xs text-neutral-400 mt-1">
              <span>5%</span>
              <span>50%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="rounded-2xl bg-red-50 border border-red-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-xl bg-red-100 p-2.5 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-red-900">Danger Zone</h2>
        </div>

        <p className="text-sm text-red-700 mb-4">
          Once you delete your account, there is no going back. All your data will be permanently removed.
        </p>

        <button
          onClick={() => setShowDeleteModal(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-red-300 bg-white px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
          Delete Account
        </button>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-neutral-900 mb-2">Delete Account</h2>
            <p className="text-neutral-600 mb-4">
              This action cannot be undone. All your devices will be unlinked and your data will be permanently deleted.
            </p>
            <p className="text-sm text-neutral-600 mb-4">
              Type <strong>DELETE</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm mb-4 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirm('');
                }}
                className="rounded-xl bg-neutral-100 px-5 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteAccount.mutate()}
                disabled={deleteConfirm !== 'DELETE' || deleteAccount.isPending}
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteAccount.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 inline-block animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowUpgradeModal(false)}
          />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-neutral-900 mb-2">Upgrade Your Plan</h2>
            <p className="text-neutral-600 mb-6">
              Choose a plan that fits your needs.
            </p>

            {/* Billing Cycle Toggle */}
            <div className="flex items-center justify-center gap-3 p-1.5 rounded-xl bg-neutral-100 mb-6">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-neutral-900 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  billingCycle === 'annual'
                    ? 'bg-white text-neutral-900 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                Annual
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  Save 30%
                </span>
              </button>
            </div>

            {/* Plan Options */}
            <div className="space-y-3 mb-6">
              <button
                onClick={() => setSelectedPlan('starter')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === 'starter'
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900">Starter</h3>
                    <p className="text-sm text-neutral-500">10 servers, 30-day history</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-neutral-900">
                      ${getPlanPrice('starter', billingCycle)}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {billingCycle === 'monthly' ? '/month' : '/year'}
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setSelectedPlan('professional')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === 'professional'
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900">Professional</h3>
                    <p className="text-sm text-neutral-500">20 servers, AI insights, priority support</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-neutral-900">
                      ${getPlanPrice('professional', billingCycle)}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {billingCycle === 'monthly' ? '/month' : '/year'}
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 rounded-xl bg-neutral-100 px-5 py-3 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpgrade(selectedPlan)}
                disabled={!paddleLoaded}
                className="flex-1 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 py-3 text-sm font-medium text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              >
                {paddleLoaded ? 'Continue to Payment' : 'Loading...'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
